// src/scenes/PerfomanceDashboard/Dashboard.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tooltip,
  Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import PieChart from "../../components/PieChart";
import StatBox from "../../components/StatBox";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { fetchDataEngine } from "../../data/Axios/DynamicService";
import Link from "@mui/material/Link";
import { useNavigate } from "react-router-dom";
import OrderTable from "./OrdersTable";
import MainCard from "./MainCard";
import { reportsManagementUrl, userManagementUrl } from "../../config";
import {
  ArrowBackIos,
  ArrowForwardIos,
  PaidOutlined,
} from "@mui/icons-material";
import BarChart from "../../components/BarChart";
import LineChart from "../../components/LineChart";
import {
  fetchJson,
  fetchJsonDynamic,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import { AlertTriangleIcon, TrendingUpIcon } from "lucide-react";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";

const baseUrl3 = userManagementUrl.uri;

// Define tour steps for Dashboard
const dashboardTourSteps = [
  {
    target: ".dashboard-container",
    content: "This dashboard displays key loan performance metrics, including Disbursed Amount, Collection Rate, Portfolio at Risk, and an Executive Summary.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".dashboard-disbursed-amount",
    content: "The Disbursed Amount card shows the total loan amount disbursed, helping you track lending activity.",
    placement: "top",
  },
  {
    target: ".dashboard-collection-rate",
    content: "The Collection Rate card displays the percentage of loans collected, a key indicator of repayment performance.",
    placement: "top",
  },
  {
    target: ".dashboard-portfolio-at-risk",
    content: "The Portfolio at Risk card highlights the percentage of the loan portfolio at risk, crucial for risk management.",
    placement: "top",
  },
  {
    target: ".dashboard-performance-rates",
    content: "The Performance Rates Overview section shows charts (Line or Bar) to visualize loan performance trends over time.",
    placement: "top",
  },
  {
    target: ".dashboard-executive-summary",
    content: "The Executive Summary lists additional performance metrics, such as achieved vs. target values, for a comprehensive overview.",
    placement: "top",
  },
];

const Dashboard = ({ datum, suppressTour, onMetricsUpdate  }) => {
  const [currentChartIndex, setCurrentChartIndex] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const [NoData, setNoData] = useState(false);
  const [LinchartData, setLinchartData] = useState([]);
  const [task, setTasks] = useState([]);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const base_url = reportsManagementUrl.uri;

  const [loading, setLoading] = useState(true);
  const charts = [
    <LineChart key="chart1" data={datum} />,
    <BarChart key="chart2" data={datum} />,
  ];

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = "/dashboard"; // Adjust if the Dashboard has a specific route

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath) && !suppressTour) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath, suppressTour]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

   // Inside the useEffect that fetches data, add:
  useEffect(() => {
    async function fetchCardsData() {
      try {
        setNoData(false);
        const linechartData = await fetchJsonDynamic(
          "perfomanceCards",
          "perfomanceCards",
          datum,
          baseUrl3
        );
        if (linechartData) {
          const linData = JSON.parse(JSON.parse(linechartData.results));
          setData(linData);
          
          // Notify parent component about the metrics data
          if (onMetricsUpdate) {
            onMetricsUpdate(linData);
          }
          
          if (linData.length === 0) {
            setNoData(true);
          }
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchCardsData();
  }, [datum, onMetricsUpdate]);

  const handlePrev = () => {
    setCurrentChartIndex((prev) => (prev === 0 ? charts.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentChartIndex((prev) => (prev === charts.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      {/* Joyride Tour */}
      <Joyride
        steps={dashboardTourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            backgroundColor: colors.primary[400],
            textColor: colors.grey[100],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard provides key loan performance metrics. Would you like a guided tour to explore its features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <div>Loading...</div>
      ) : !data || data.length === 0 || NoData === true ? (
        <div>No Data Available for display...</div>
      ) : (
        <Grid container rowSpacing={2.5} columnSpacing={2.75} padding={"1rem"} className="dashboard-container">
          {/* row 1 */}
          {data ? (
            <>
              <Grid item xs={12} sx={{ mb: -2.25 }}>
                <Header
                  title="Performance Dashboard"
                  subtitle="Welcome to Performance Dashboard"
                />
              </Grid>
              {data.map((item, index) => (
                <React.Fragment key={index}>
                  {item.title === "Loan Amount" && (
                    <Grid item xs={12} sm={6} md={6} lg={4} className="dashboard-disbursed-amount">
                      <Tooltip
                        title={item.Description || ""}
                        placement="top"
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                      >
                        <Link
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Box
                            gridColumn="span 4"
                            backgroundColor={colors.primary[400]}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="0.5rem"
                            padding="15px"
                            boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                          >
                            <StatBox
                              title={""}
                              subtitle="Disbursed Amount"
                              progress={
                                item.achieved != null && item.target
                                  ? (
                                      (item.achieved / item.target) *
                                      100
                                    ).toFixed(1)
                                  : 0
                              }
                              achieved={
                                Number(
                                  item.achieved.toFixed(0)
                                ).toLocaleString() ?? 0
                              }
                              target={
                                Number(
                                  item.target.toFixed(0)
                                ).toLocaleString() ?? 0
                              }
                              remaining={
                                Number(
                                  item.remaining.toFixed(0)
                                ).toLocaleString() ?? 0
                              }
                              measurementName="Disbursed Amount"
                              increase={`+${
                                item.uploadedStatementstPercentage ?? 0
                              }%`}
                              icon={
                                <PaidOutlined
                                  sx={{
                                    color: colors.greenAccent[400],
                                    fontSize: "26px",
                                  }}
                                />
                              }
                            />
                          </Box>
                        </Link>
                      </Tooltip>
                    </Grid>
                  )}
                  {item.title === "Collection Rate" && (
                    <Grid item xs={12} sm={6} md={6} lg={4} className="dashboard-collection-rate">
                      <Tooltip
                        title={item.Description || ""}
                        placement="top"
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                      >
                        <Link
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Box
                            gridColumn="span 4"
                            backgroundColor={colors.primary[400]}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="0.5rem"
                            padding="15px"
                            boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                          >
                            <StatBox
                              title={""}
                              subtitle="Collection Rate"
                              progress={
                                item.achieved != null && item.target
                                  ? (
                                      (item.achieved / item.target) *
                                      100
                                    ).toFixed(1)
                                  : "0.0"
                              }
                              achieved={item.achieved ?? 0}
                              target={item.target ?? 0}
                              remaining={item.remaining ?? 0}
                              measurementName="Collection Rate"
                              increase={`+${
                                item.userGrowthRatePercentage ?? 0
                              }%`}
                              icon={
                                <TrendingUpIcon
                                  sx={{
                                    color: colors.greenAccent[400],
                                    fontSize: "26px",
                                  }}
                                />
                              }
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: isSmallScreen ? "0.75rem" : "1rem",
                                },
                              }}
                            />
                          </Box>
                        </Link>
                      </Tooltip>
                    </Grid>
                  )}
                  {item.title === "Portfolio at Risk" && (
                    <Grid item xs={12} sm={6} md={6} lg={4} className="dashboard-portfolio-at-risk">
                      <Tooltip
                        title={item.Description || ""}
                        placement="top"
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                      >
                        <Box
                          gridColumn="span 4"
                          backgroundColor={colors.primary[400]}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="0.5rem"
                          padding="15px"
                          boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                          sx={{ cursor: item.Description ? "help" : "default" }}
                        >
                          <StatBox
                            subtitle="Portfolio At Risk"
                            progress={
                              item != null && item.progress
                                ? (
                                    ((100 - item.achieved) /
                                      (100 - item.target)) *
                                    100
                                  ).toFixed(1)
                                : "0.0"
                            }
                            achieved={item.achieved ?? 0}
                            target={item.target ?? 0}
                            remaining={item.remaining ?? 0}
                            measurementName="Portfolio At Risk"
                            icon={
                              <AlertTriangleIcon
                                sx={{
                                  color: colors.greenAccent[400],
                                  fontSize: "26px",
                                }}
                              />
                            }
                            sx={{
                              "& .MuiTypography-root": {
                                fontSize: isSmallScreen ? "0.75rem" : "1rem",
                              },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                  )}
                </React.Fragment>
              ))}

              <Grid
                item
                md={8}
                sx={{ display: { sm: "none", md: "block", lg: "none" } }}
              />

              <Grid item xs={12} md={7} lg={8} className="dashboard-performance-rates">
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item>
                    <Typography variant="h5">
                      Performance Rates Overview
                    </Typography>
                  </Grid>
                  <Grid item>
                    <IconButton onClick={handlePrev}>
                      <ArrowBackIos />
                    </IconButton>
                    <IconButton onClick={handleNext}>
                      <ArrowForwardIos />
                    </IconButton>
                  </Grid>
                </Grid>

                <Box
                  sx={{ mt: 1 }}
                  height="400px"
                  backgroundColor={colors.primary[400]}
                  borderRadius="0.5rem"
                  boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {charts[currentChartIndex]}
                </Box>
              </Grid>
              <Grid item xs={12} md={5} lg={4} className="dashboard-executive-summary">
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item>
                    <Typography variant="h5">Executive Summary</Typography>
                  </Grid>
                </Grid>
                <MainCard sx={{ mt: 1, height: "400px" }} content={false}>
                  <Box
                    sx={{
                      mt: 1,
                      height: "400px",
                      overflow: "auto",
                      backgroundColor: colors.primary[400],
                      borderRadius: "8px",
                      boxShadow: `0 4px 8px ${colors.grey[900]}`,
                      p: 3,
                    }}
                  >
                    {data.map((item, index) => {
                      const shouldRender =
                        item.title !== "Collection Rate" &&
                        item.title !== "Portfolio at Risk" &&
                        item.title !== "Loan Amount";

                      return shouldRender ? (
                        <Tooltip
                          key={index}
                          title={item.Description || ""}
                          placement="top"
                          arrow
                          enterTouchDelay={0}
                          leaveTouchDelay={3000}
                        >
                          <Box
                            sx={{
                              mb: 3,
                              p: 2,
                              borderRadius: "4px",
                              backgroundColor: colors.grey[800],
                              "&:hover": {
                                backgroundColor: colors.primary[200],
                              },
                              cursor: "pointer",
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{ color: colors.grey[100] }}
                              >
                                {item.title}
                              </Typography>
                              {item.measurementName && (
                                <Typography
                                  variant="subtitle2"
                                  sx={{ color: colors.grey[400] }}
                                >
                                  {item.measurementName}
                                </Typography>
                              )}
                            </Box>

                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mt={2}
                            >
                              <Box textAlign="center">
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: colors.greenAccent[400],
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.achieved}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: colors.grey[300] }}
                                >
                                  Achieved
                                </Typography>
                              </Box>

                              <Box textAlign="center">
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: colors.greenAccent[500],
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.target}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: colors.grey[300] }}
                                >
                                  Target
                                </Typography>
                              </Box>

                              <Box textAlign="center">
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: colors.redAccent[400],
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.remaining}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: colors.grey[300] }}
                                >
                                  Remaining
                                </Typography>
                              </Box>
                            </Box>

                            <Box mt={2}>
                              <Box
                                height="6px"
                                borderRadius="3px"
                                sx={{ backgroundColor: colors.grey[700] }}
                              >
                                <Box
                                  height="100%"
                                  width={`${Math.min(
                                    100,
                                    (item.achieved / item.target) * 100
                                  )}%`}
                                  borderRadius="3px"
                                  sx={{
                                    backgroundColor:
                                      item.achieved / item.target > 0.7
                                        ? colors.greenAccent[500]
                                        : item.achieved / item.target > 0.4
                                        ? colors.blueAccent[500]
                                        : colors.redAccent[500],
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      ) : null;
                    })}
                  </Box>
                </MainCard>
              </Grid>
            </>
          ) : (
            <Typography variant="h5">No data</Typography>
          )}
        </Grid>
      )}
    </>
  );
};

export default Dashboard;